import type { ReactNode } from 'react';
import { PairingMark } from '../../brand/PairingMark';
import { PoweredBy } from '../../brand/PoweredBy';
import ui from '../../components/ui.module.css';
import d from '../../features/demos/designer.module.css';
import f from '../../features/features.module.css';
import { TenantTheme } from '../../theme/TenantTheme';
import type { client } from '../content/client';
import type { reps } from '../content/reps';
import { Pill } from './Pill';
import v from './visuals.module.css';

/**
 * Illustrations built from the portal's own components and styles, with fictional data: the visuals are the proof,
 * so they look like the product. Nothing here is interactive: buttons and chips are drawn.
 */

function Figure({ label, className, children }: { label: string; className?: string; children: ReactNode }) {
  return (
    <figure className={`${v.figure} ${className ?? ''}`}>
      {children}
      <figcaption className="visually-hidden">{label}</figcaption>
    </figure>
  );
}

/** A drawn button: the portal's button styles on a span, so it reads as an illustration, not a control. */
function DrawnButton({ children, primary }: { children: string; primary?: boolean }) {
  return <span className={`${ui.button} ${primary ? ui.primary : ui.secondary} ${v.drawn}`}>{children}</span>;
}

type HeroVisual = (typeof client)['hero']['visual'];

/** Client hero: a picklist change caught overnight, a HubSpot task in the morning, then fixed. The held pill turns synced. */
export function DriftTimeline({ visual }: { visual: HeroVisual }) {
  const [tuesday, overnight, morning, fix] = visual.steps;
  return (
    <Figure label={visual.label} className={v.timelineFigure}>
      <ol className={v.timeline}>
        <li className={v.step}>
          <span className={v.when}>{tuesday!.when}</span>
          <p>{tuesday!.text}</p>
        </li>
        <li className={v.step}>
          <span className={v.when}>{overnight!.when}</span>
          <p>{overnight!.text}</p>
        </li>
        <li className={`${v.step} ${v.stepTask}`}>
          <span className={v.when}>{morning!.when}</span>
          <div className={v.taskCard}>
            <div className={v.taskHead}>
              <span className={v.taskApp}>{visual.task.app}</span>
              <span className={v.taskKind}>{visual.task.kind}</span>
            </div>
            <p className={v.taskTitle}>{visual.task.title}</p>
            <span className={v.pillSwap}>
              <Pill tone="held" className={v.pillFrom}>
                {visual.task.held}
              </Pill>
              <Pill tone="synced" className={v.pillTo}>
                {visual.task.synced}
              </Pill>
            </span>
          </div>
        </li>
        <li className={`${v.step} ${v.stepDone}`}>
          <span className={v.when}>{fix!.when}</span>
          <p>{fix!.text}</p>
        </li>
      </ol>
    </Figure>
  );
}

type HeldVisual = (typeof client)['heldRecords']['visual'];

/** A held cause, its value and fixes, as the portal shows them (features/held/HeldCausePage.tsx). */
export function HeldGroupIllustration({ visual }: { visual: HeldVisual }) {
  return (
    <Figure label={visual.label}>
      <div className={`${ui.panel} ${v.held}`}>
        <div className={v.heldHead}>
          <p className={v.cardTitle}>{visual.title}</p>
          <Pill tone="held">{visual.pill}</Pill>
        </div>
        <p className={v.meta}>
          {visual.mapping} · {visual.oldest}
        </p>
        <p className={v.reason}>{visual.reason}</p>
        <ul className={f.valueList}>
          <li className={f.valueRow}>
            <code>{visual.value}</code>
            <span className={v.meta}>{visual.valueCount}</span>
          </li>
        </ul>
        <div className={v.actions}>
          {visual.fixes.map((label) => (
            <DrawnButton key={label}>{label}</DrawnButton>
          ))}
        </div>
        <div className={v.replay}>
          <DrawnButton primary>{visual.replay}</DrawnButton>
        </div>
      </div>
    </Figure>
  );
}

type ShadowVisual = (typeof client)['shadow']['visual'];

/** A shadow report summary: the four counts, and one difference opened up. */
export function ShadowSummary({ visual }: { visual: ShadowVisual }) {
  const d = visual.difference;
  return (
    <Figure label={visual.label}>
      <div className={`${ui.panel} ${v.shadow}`}>
        <p className={v.cardTitle}>{visual.title}</p>
        <ul className={v.counts} aria-label={visual.summary}>
          {visual.counts.map((c) => (
            <li key={c.label} className={v.count}>
              <span className={`${v.countValue} tabular`} data-tone={c.tone}>
                {c.value}
              </span>
              <span className={v.countLabel}>{c.label}</span>
            </li>
          ))}
        </ul>
        <div className={v.diff}>
          <p className={v.diffRecord}>
            <Pill tone="held">{d.pill}</Pill> {d.record}
          </p>
          <table className={v.diffTable}>
            <thead>
              <tr>
                {d.columns.map((c) => (
                  <th key={c} scope="col">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">{d.field}</th>
                <td>{d.plumb}</td>
                <td>{d.current}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className={v.note}>{visual.note}</p>
      </div>
    </Figure>
  );
}

/** A browser window around an illustration of a page. */
export function BrowserFrame({ url, children }: { url: string; children: ReactNode }) {
  return (
    <div className={v.browser}>
      <div className={v.browserBar} aria-hidden="true">
        <span className={v.dots}>
          <span />
          <span />
          <span />
        </span>
        <span className={v.address}>{url}</span>
      </div>
      <div className={v.browserBody}>{children}</div>
    </div>
  );
}

type PortalVisual = (typeof client)['portal']['visual'];

/**
 * A fictional tenant's integrations overview, in its own color (ADR D17: the tenant color replaces pear; sky and rust
 * keep their meaning), with the footer every tenant portal carries.
 */
export function PortalOverviewIllustration({ visual }: { visual: PortalVisual }) {
  return (
    <Figure label={visual.label}>
      <BrowserFrame url={visual.url}>
        <TenantTheme primaryColor={visual.tenantColor} className={v.portal}>
          <div className={v.portalBar}>
            <div className={v.portalBarStart}>
              <p className={v.tenant}>
                <span className={v.tenantMark} aria-hidden="true">
                  {visual.tenant.charAt(0)}
                </span>
                {visual.tenant}
              </p>
              <p className={v.portalHeading}>{visual.heading}</p>
            </div>
            <PoweredBy />
          </div>
          <ul className={v.portalNav}>
            {visual.nav.map((n, i) => (
              <li key={n} className={i === 0 ? v.portalNavActive : undefined}>
                {n}
              </li>
            ))}
          </ul>
          <div className={v.portalMain}>
            <p className={v.meta}>{visual.description}</p>
            <ul className={f.cards}>
              {visual.mappings.map((m) => (
                <li key={m.name} className={f.card}>
                  <div>
                    <p className={v.mappingName}>{m.name}</p>
                    <div className={f.cardMeta}>
                      {m.meta.map((x) => (
                        <span key={x}>{x}</span>
                      ))}
                    </div>
                  </div>
                  <div className={f.cardSide}>
                    <div className={f.pills}>
                      {m.pills.map((pill) => (
                        <Pill key={pill.text} tone={pill.tone}>
                          {pill.text}
                        </Pill>
                      ))}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </TenantTheme>
      </BrowserFrame>
    </Figure>
  );
}

type StudioVisual = (typeof reps)['hero']['visual'];

/**
 * Demo Studio's chat, mid-design: the rep's message, the designer's answer and the chips for the next open question,
 * with the demo taking shape in a "Your demo" card. The designer's own styles
 * (features/demos/designer.module.css), so it looks like the page reps use; the chips and buttons are drawn.
 */
export function DemoStudioIllustration({ visual }: { visual: StudioVisual }) {
  const p = visual.preview;
  return (
    <Figure label={visual.label}>
      <div className={`${ui.panel} ${v.studio}`}>
        <p className={v.studioTitle}>
          <PairingMark variant="light" height={20} decorative />
          {visual.title}
          <span className={d.chipHint}>{visual.prospect}</span>
        </p>
        <ol className={d.turns}>
          <li className={d.said}>
            <p>{visual.said}</p>
          </li>
          <li className={d.answer}>
            <p>{visual.answer}</p>
          </li>
        </ol>
        <div>
          <p className={d.chipQuestion}>{visual.question}</p>
          <div className={d.chipRow}>
            {visual.chips.map((label) => (
              <span key={label} className={`${d.chip} ${v.drawn}`}>
                {label}
              </span>
            ))}
          </div>
        </div>
        <div className={v.studioPreview}>
          <div className={v.studioPreviewHead}>
            <p className={v.studioPreviewTitle}>{p.title}</p>
            <div className={d.flow}>
              <span className={d.system}>{p.system}</span>
              <span className={d.arrow}>{p.arrow}</span>
              <span className={d.system}>HubSpot</span>
              <span className={d.chipHint}>{p.frequency}</span>
            </div>
          </div>
          <ul className={v.studioObjects}>
            {p.objects.map((o) => (
              <li key={o.label}>
                <span className={v.studioObjectLabel}>{o.label}</span>
                <span className={d.target}>
                  {p.arrow} {o.target}
                </span>
              </li>
            ))}
          </ul>
          <p className={d.creates}>{p.creates}</p>
        </div>
      </div>
    </Figure>
  );
}

type Exchange = (typeof reps)['fitOnCall']['example'];

/** A short chat transcript, labelled as an example. */
export function ChatExchange({ example }: { example: Exchange }) {
  return (
    <figure className={v.chat}>
      <figcaption className={v.chatLabel}>{example.label}</figcaption>
      <ul className={v.chatList}>
        {example.messages.map((m, i) => (
          <li key={m.who} className={i === 0 ? v.msgRep : v.msgFit}>
            <span className={v.msgWho}>{m.who}</span>
            <p>{m.text}</p>
          </li>
        ))}
      </ul>
    </figure>
  );
}
